// src/Login.js
import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import API_BASE_URL from './utils/config';

export default function Login() {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const sendLocationToBackend = async (token) => {
    
  if (!navigator.geolocation) {
    console.warn("Geolocation not supported");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      const { latitude, longitude, accuracy } = pos.coords;

      try {
        await axios.post(`${API_BASE_URL}/api/location`,
          { latitude, longitude, accuracy },
          {headers: {Authorization: `Bearer ${token}`,},}
        );
      } catch (err) {
        console.error("Failed to save location:", err);
      }
    },
    (err) => {
      console.warn("Location permission denied:", err.message);
      // ❗ Do NOT block login
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    }
  );
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/login`, { email, password });
      
      login(res.data.user, res.data.token, remember);
      //const userId = req.user.id;

      sendLocationToBackend(res.data.token);
      navigate('/dashboard');
    } catch (err) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        'Invalid credentials. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-900 via-sky-800 to-sky-900 text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-10 border-b border-white/10 bg-sky-900/70 backdrop-blur">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-white/10 grid place-items-center ring-1 ring-white/15">
              {/* Simple logo block */}
              <span className="text-lg font-black">W</span>
            </div>
            <div>
              <p className="text-lg font-bold leading-tight">
                WECARE <span className="text-sky-300">INDIA Agency</span>
              </p>
              <p className="text-xs text-white/70 -mt-1">Pharma / Medical ERP</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-sm">
            <a
              href="https://maps.app.goo.gl/aZnzxvP8nhfexkwe8"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white text-white/80"
            >
              View on Google Maps
            </a>
            <span className="text-white/60">•</span>
            <a href="tel:01482457979" className="hover:text-white text-white/80">
              01482-457979
            </a>
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Left: brand/story (hidden on small) */}
          <div className="relative hidden lg:block">
            <div className="absolute inset-0 -translate-x-8 -translate-y-6 blur-3xl opacity-30 bg-gradient-to-tr from-sky-400 to-indigo-300 rounded-3xl" />
            <div className="relative h-full rounded-3xl bg-white/5 ring-1 ring-white/10 p-8 flex flex-col justify-between">
              <div>
                <h2 className="text-3xl font-extrabold">
                  Welcome to <span className="text-sky-300">WECARE INDIA Agency</span>
                </h2>
                <p className="mt-2 text-white/80">
                  Pharma / Medical software for distributors, super-stockists and C&amp;F.
                </p>
                <ul className="mt-8 space-y-4 text-white/90 text-sm">
                  <li className="flex gap-3">
                    <span className="mt-0.5 h-2 w-2 rounded-full bg-sky-300"></span>
                    <div>
                      <p className="font-semibold">Batch &amp; Expiry</p>
                      <p className="text-white/75">Auto alerts, FIFO picking and returns handling.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 h-2 w-2 rounded-full bg-sky-300"></span>
                    <div>
                      <p className="font-semibold">GST / e-Invoice</p>
                      <p className="text-white/75">HSN categories, IRN-ready exports & e-Way data prep.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 h-2 w-2 rounded-full bg-sky-300"></span>
                    <div>
                      <p className="font-semibold">Role Security</p>
                      <p className="text-white/75">Sales, Warehouse, CRM, Admin with least-privilege.</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-10 rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 text-sm">
                <p>
                  <span className="text-white/70">Phone:</span>{' '}
                  <a href="tel:01482457979" className="hover:text-white">
                    01482-457979
                  </a>
                </p>
                <p className="mt-2">
                  <a
                    className="hover:text-white underline underline-offset-4"
                    href="https://maps.app.goo.gl/aZnzxvP8nhfexkwe8"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Find us on Google Maps
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Right: login card */}
          <div className="relative">
            <div className="absolute inset-0 -translate-x-4 -translate-y-6 blur-3xl opacity-25 bg-gradient-to-tr from-sky-400 to-indigo-300 rounded-3xl" />
            <div className="relative rounded-3xl bg-white/5 ring-1 ring-white/10 p-6 sm:p-8 lg:p-10 shadow-xl">
              <div className="mb-6">
                <h1 className="text-2xl font-bold">Log in</h1>
                <p className="text-sm text-white/70 mt-1">
                  Access your dashboard to manage stock, orders & invoices.
                </p>
              </div>

              {error && (
                <div className="mb-4 rounded-xl bg-red-500/15 text-red-200 ring-1 ring-red-400/20 px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label className="text-sm text-white/80">Email</label>
                  <input
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full rounded-xl bg-white/10 ring-1 ring-white/15 px-3 py-3 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-300"
                    placeholder="you@company.com"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-white/80">Password</label>
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="text-xs text-white/70 hover:text-white"
                    >
                      {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <input
                    type={showPw ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full rounded-xl bg-white/10 ring-1 ring-white/15 px-3 py-3 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-300"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="inline-flex items-center gap-2 select-none">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="h-4 w-4 rounded border-white/20 bg-white/10"
                    />
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="text-sky-200 hover:text-white">
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-300 text-sky-950 px-5 py-3 font-semibold hover:bg-sky-200 disabled:opacity-70"
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>

                {/* Optional: show register if you enable it later */}
                {/* <p className="text-sm text-white/70 text-center">
                  New here? <Link to="/register" className="text-sky-200 hover:text-white">Create an account</Link>
                </p> */}
                <p className="text-xs text-white/60 text-center mt-2">
                  By continuing you agree to our Terms & Privacy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Simple footer */}
      <footer className="border-t border-white/10">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6 text-xs text-white/60 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} WECARE INDIA Agency. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="https://maps.app.goo.gl/aZnzxvP8nhfexkwe8" target="_blank" rel="noreferrer" className="hover:text-white">
              Google Maps
            </a>
            <a href="tel:01482457979" className="hover:text-white">
              01482-457979
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
